import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, User, Car, Clock, Navigation, Phone, DollarSign, Moon, Sun } from "lucide-react";
import RideTrackingOSMap from "@/components/Map/RideTrackingOSMap";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { useTheme } from "@/hooks/useTheme";
// Authentication removed: no cookies or booking validation

interface TripAddress {
  markerType: string;
  title: string;
  address: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

interface Customer {
  _id: string;
  name: string;
  phone: string;
  email: string;
  avatar?: string;
  rating?: number;
}

interface Driver {
  _id: string;
  name: string;
  phone: string;
  email: string;
  avatar?: string;
  rating?: number;
  location?: {
    coordinates: [number, number];

  };
  vehicleInfo?: Vehicle;
}

interface Vehicle {
  name: string;
  icon?: string;
  seats?: number;
  vehicleNo?: string;
  vehicleModel?: string;
}

interface Pricing {
  name: string;
  price: number;
}

interface RideDetails {
  _id: string;
  orderNo: string;
  tripStatus: string;
  wayPointsTripStatus: [],
  tripAddress: TripAddress[];
  customer: Customer;
  driver?: Driver;
  driverRouteString?: string;
  selectedVehicle: Vehicle;
  expectedBilling?: {
    km: number;
    kmText: string;
    duration: number;
    durationText: string;
    pricing: Pricing[];
    userBilling: {
      totalAmount: number;
      discount: number;
      tax: {
        percentage: number;
        taxTotal: number;
      };
    };
  };
  finalBilling?: {
    km: number;
    kmText: string;
    duration: number;
    durationText: string;
    pricing: Pricing[];
    userBilling: {
      totalAmount: number;
      discount: number;
      tax: {
        percentage: number;
        taxTotal: number;
      };
    };
  };
  driverRouteProgress?: {
    duration: number;
    durationText: string;
  };
  grandTotal: number;
  otp?: number;
  createdAt: string;
  pickedAt?: string | null;
  dropedAt?: string | null;
  acceptedAt?: string | null;
  arrivedAt?: string | null;
  scheduled?: {
    isScheduled: boolean;
    scheduledAt: string | null;
  };
  country?: {
    name: string;
    currencySymbol: string;
    currencyCode: string;
  };
}

export function RideDetailsPage() {

  const { shareId } = useParams();

  const [rideDetails, setRideDetails] = useState<RideDetails | null>(null);
  const [tripStatus, setTripStatus] = useState<string>('');
  const [duration, setDuration] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [driverLocation, setDriverLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [routeString, setRouteString] = useState<string>('');
  const [userId, setUserId] = useState<string | null>(null);
  const { theme, toggleTheme } = useTheme();
  // Single param: use shareId as the identifier for fetching ride details
  const [activeBookingId, setActiveBookingId] = useState<string | undefined>(shareId);


  useEffect(() => {
    if (!activeBookingId) return;

    if (error) {
      return;
    }

    if (tripStatus.toLowerCase() === 'completed' || tripStatus.toLowerCase() === 'canceled') {
      return;
    }

    // const intervalId = setInterval(() => {

    //   getBookingDetail(activeBookingId);
    // }, 10000);

    // return () => {
    //   clearInterval(intervalId);
    // };
  }, []);

  // WebSocket callback handlers
  const handleDriverLocationUpdate = useCallback((locationData: any) => {
    if (locationData?.location) {
      setDriverLocation(locationData.location);
      // Also update the ride details if needed
      setRideDetails(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          driver: {
            ...prev.driver!,
            location: {
              coordinates: [locationData.location.longitude, locationData.location.latitude]
            }
          }
        };
      });

    }
  }, []);

  const handleBookingStatusUpdate = useCallback((data: any) => {
    // Refetch ride details when status changes
    // if (activeBookingId) {
    //   getBookingDetail(activeBookingId);
    // }
    setTripStatus(data);
  }, [activeBookingId]);

  const handleDriverRouteUpdate = useCallback((routeData: any) => {

    setRouteString(routeData.polyline);
    setDuration(routeData.durationText);
  }, []);

  const getBookingDetail = async (id: any) => {
    try {
      if (!id) {
        return;
      }
      const baseUrl = String(process.env.BUN_PUBLIC_API_URL);

      if (!baseUrl) {
        console.error("[RideDetails] Missing BUN_PUBLIC_API_URL / BUN_PUBLIC_PRODUCTION_API_URL env.");
        setError("API base URL is not configured.");
        return;
      }

      const response = await fetch(`${baseUrl}/ride/${id}`);



      const data = await response.json();
      const rideData = data.data;

      if (data.success) {
        setRideDetails(rideData);
        setRouteString(rideData.polyline || '');
        setDuration(rideData?.duration || null);
        setTripStatus(rideData.tripStatus || '');
        setDriverLocation(rideData.driver?.location ? {
          latitude: rideData.driver.location.latitude,
          longitude: rideData.driver.location.longitude,
        } : null);
        if (rideData?.customer?._id) {
          setUserId(rideData.customer._id);
        }
        setError(null);
      } else {
        setError(data.message || "Failed to load ride details");
      }

      return data;
    } catch (error: any) {
      console.error("Error in getBookingDetail:", error);
      setError(error?.message || "Failed to load ride details");
      return error;
    }
  };


  // Initial fetch on component mount - no authentication/validation
  useEffect(() => {
    const fetchRideDetails = async () => {
      try {
        setLoading(true);

        if (shareId) {
          setActiveBookingId(shareId);
          await getBookingDetail(shareId);

          // Set userId after fetching details so WebSocket can connect
          if (rideDetails?.customer?._id) {
            setUserId(rideDetails.customer._id);
          }
        } else {
          setError("Missing shareId in URL.");
        }
      } catch (err) {
        setError("Failed to load ride details. The link may be invalid or expired.");
        console.error("Error fetching ride details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchRideDetails();
    }
  }, [shareId]);

  const getTripStatusColor = (status: any) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "picked":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "arrived":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "ontheway":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "finding_driver":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
      case "canceled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getTripStatusText = (status: any) => {
    return status?.replace(/_/g, " ").replace(/\b\w/g, (l: any) => l.toUpperCase());
  };

  // Note: Removed polling - now using WebSocket for real-time updates

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading ride details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if ride is completed or canceled - show expired message
  if ((rideDetails && (tripStatus.toLowerCase() === 'completed' || tripStatus.toLowerCase() === 'canceled')) || (tripStatus === 'completed' || tripStatus === 'canceled')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4 border-orange-200 dark:border-orange-800">
          <CardContent className="p-8 text-center">
            <div className="text-orange-600 dark:text-orange-400 text-5xl mb-4">🔒</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Link Expired
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This ride has been {tripStatus.toLowerCase()} and the tracking link is no longer active.
            </p>
            <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${getTripStatusColor(tripStatus)}`}>
              {getTripStatusText(tripStatus)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4 border-red-200 dark:border-red-800">
          <CardContent className="p-8 text-center">
            <div className="text-red-600 dark:text-red-400 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Unable to Load Ride
            </h2>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <WebSocketProvider
      userId={userId || undefined}
      driverId={rideDetails?.driver?._id || undefined}
      onDriverLocationUpdate={handleDriverLocationUpdate}
      onBookingStatusUpdate={handleBookingStatusUpdate}
      onDriverRouteUpdate={handleDriverRouteUpdate}
    >
      <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card className="border-none shadow-lg">
            <CardHeader className="bg-linear-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-2xl font-bold mb-2">🚗 Ride Details</CardTitle>
                  <p className="text-blue-100 text-sm">Order No: {rideDetails?.orderNo}</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getTripStatusColor(tripStatus)}`}>
                  {tripStatus ? getTripStatusText(tripStatus) : getTripStatusText(rideDetails?.tripStatus)}
                </span>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="inline-flex items-center rounded-full border border-gray-300 p-2 bg-white text-sm font-medium text-gray-900 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
              </div>
            </CardHeader>
          </Card>

          {/* Map with Route and Driver Tracking */}
          {/* {
            rideDetails?.tripStatus !== 'finding_driver' && */}
          <Card className="shadow-lg overflow-hidden">
            <CardContent className="p-0">
              {/* Time Information - Above Map */}
              {
                (tripStatus !== 'arrived' && tripStatus !== 'finding_driver') &&
                <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <div className="bg-white flex gap-2 dark:bg-gray-700 rounded-md p-3 shadow text-xs sm:text-sm flex-1">
                    <p className="text-blue-600 dark:text-blue-400 font-medium mb-1">
                      {
                        tripStatus === 'picked'
                          ? `Trip completed in`
                          : tripStatus === 'arrived'
                            ? "Driver has arrived"
                            : tripStatus !== 'finding_driver'

                              ? `Driver arriving in` : ""
                      }
                    </p>
                    <p className="text-gray-800 dark:text-gray-200 font-semibold">
                      {(tripStatus !== 'arrived' && tripStatus !== 'finding_driver') && (() => {
                        if (!duration) return 'a few sec';
                        const totalMinutes = parseFloat(String(duration));
                        if (isNaN(totalMinutes) || totalMinutes < 0) return 'a few sec';
                        if (totalMinutes < 1) return 'less than a min';
                        const hours = Math.floor(totalMinutes / 60);
                        const mins = Math.round(totalMinutes % 60);
                        if (hours > 0) return `${hours} hr${hours > 1 ? 's' : ''} ${mins > 0 ? `${mins} min` : ''}`.trim();
                        return `${Math.round(totalMinutes)} min`;
                      })()}
                    </p>
                    {rideDetails && rideDetails?.wayPointsTripStatus?.length > 0 && (() => {
                      const lastStatus: any =
                        rideDetails.wayPointsTripStatus[
                        rideDetails.wayPointsTripStatus.length - 1
                        ];

                      return (
                        <div>
                          {lastStatus.tripStatus === "arrived" && lastStatus.arrivedAt && (
                            <p>(Arrived at stop {rideDetails.wayPointsTripStatus.length})</p>
                          )}

                          {lastStatus.tripStatus === "picked" && lastStatus.pickedAt && (
                            <p>(Picked at stop {rideDetails.wayPointsTripStatus.length})</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>




                </div>
              }


              {/* Map Container */}

            </CardContent>
          </Card>
          {/* } */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Trip Map</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full overflow-hidden" style={{ height: "320px" }}>
                <RideTrackingOSMap
                  tripAddress={rideDetails?.tripAddress || []}
                  wayPointsTripStatus={rideDetails?.wayPointsTripStatus || []}
                  driverLocation={driverLocation
                    //   || {
                    //   latitude: rideDetails?.driver?.location?.latitude|| 0,
                    //   longitude: rideDetails?.driver?.location?.longitude || 0,
                    // }
                  }
                  driverRouteString={routeString || ''}
                  showRoute={true}
                  // setDuration={setDuration}
                  height="100%"
                />
              </div>
            </CardContent>
          </Card>
          {/* Locations */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                Trip Route
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pickup Location */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  {rideDetails?.tripAddress && rideDetails.tripAddress.length > 1 && (
                    <div className="w-0.5 h-16 bg-gray-300"></div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Pickup Location</p>

                  <p className="ont-medium text-gray-900 dark:text-gray-100">
                    {rideDetails?.tripAddress?.[0]?.address}
                  </p>
                </div>
              </div>

              {/* Intermediate Stops (if more than 2 addresses) */}
              {rideDetails?.tripAddress && rideDetails.tripAddress.length > 2 && (
                <>
                  {rideDetails.tripAddress.slice(1, -1).map((address, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <div className="w-0.5 h-16 bg-gray-300"></div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Stop {index + 1}</p>
                        {/* <p className="font-medium text-gray-900 dark:text-gray-100">
                        {address?.title}
                      </p> */}
                        <p className="ont-medium text-gray-900 dark:text-gray-100">
                          {address?.address}
                        </p>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Drop Location (if exists and different from pickup) */}
              {rideDetails?.tripAddress && rideDetails.tripAddress.length > 1 && (
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Drop-off Location</p>

                    <p className="ont-medium text-gray-900 dark:text-gray-100">
                      {rideDetails.tripAddress[rideDetails.tripAddress.length - 1]?.address}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Passenger Info */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Passenger Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-linear-to-br from-green-500 to-teal-500 flex items-center justify-center text-white text-2xl font-bold">
                  {rideDetails?.customer?.avatar ? (
                    <img
                      src={rideDetails.customer.avatar}
                      alt={rideDetails.customer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    rideDetails?.customer?.name?.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {rideDetails && rideDetails?.customer?.name?.charAt(0).toUpperCase() + rideDetails?.customer?.name?.slice(1)}
                  </h3>
                  {rideDetails?.customer?.rating && (
                    <div className="flex items-center gap-1 text-yellow-500 mt-1">
                      <span>⭐</span>
                      <span className="text-gray-700 dark:text-gray-300">{rideDetails?.customer.rating.toFixed(1)}</span>
                    </div>
                  )}
                  {/* <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {rideDetails?.customer?.phone}
                  </p> */}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Driver Info */}
          {(rideDetails?.driver && tripStatus !== "finding_driver") && (
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Driver Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-linear-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-2xl font-bold">
                    {rideDetails?.driver?.avatar ? (
                      <img
                        src={rideDetails.driver.avatar}
                        alt={rideDetails.driver.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      rideDetails?.driver.name?.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                      {rideDetails?.driver.name.charAt(0).toUpperCase() + rideDetails?.driver.name.slice(1)}
                    </h3>

                    {rideDetails?.driver.rating && (
                      <div className="flex items-center gap-1 text-yellow-500 mt-1">
                        <span>⭐</span>
                        <span className="text-gray-700 dark:text-gray-300">{rideDetails?.driver.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vehicle Info */}
          {(rideDetails?.selectedVehicle && tripStatus !== "finding_driver") && (
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-600" />
                  Vehicle Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Vehicle Name</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{rideDetails?.selectedVehicle?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Seats</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{rideDetails?.selectedVehicle?.seats} passengers</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">

                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Vehicle Number</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {rideDetails?.driver?.vehicleInfo?.vehicleNo}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Vehicle Model</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {rideDetails?.driver?.vehicleInfo?.vehicleModel}
                    </p>
                  </div>
                </div>


              </CardContent>
            </Card>
          )}

          {/* Trip Info */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Trip Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {(rideDetails?.expectedBilling?.km || rideDetails?.finalBilling?.km) && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Distance</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {rideDetails?.finalBilling?.kmText || rideDetails?.expectedBilling?.kmText}
                    </p>
                  </div>
                )}
                {(rideDetails?.expectedBilling?.duration || rideDetails?.finalBilling?.duration) && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Duration</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {rideDetails?.finalBilling?.durationText || rideDetails?.expectedBilling?.durationText}
                    </p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Booked At</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {rideDetails && new Date(rideDetails?.createdAt).toLocaleString()}
                  </p>
                </div>

              </div>
            </CardContent>
          </Card>

          {/* Billing Info */}
          {(rideDetails?.expectedBilling || rideDetails?.finalBilling) && (
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Fare Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(rideDetails?.finalBilling?.pricing || rideDetails?.expectedBilling?.pricing)?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {rideDetails?.country?.currencySymbol || '$'}{Math.abs(item.price).toFixed(2)}
                        {item.price < 0 && <span className="text-green-600 ml-1">(Discount)</span>}
                      </span>
                    </div>
                  ))}
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span className="text-gray-900 dark:text-gray-100">Total</span>
                      <span className="text-blue-600 dark:text-blue-400">
                        {rideDetails?.country?.currencySymbol || '$'}
                        {(rideDetails?.finalBilling?.userBilling?.totalAmount ||
                          rideDetails?.expectedBilling?.userBilling?.totalAmount ||
                          rideDetails?.grandTotal)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          {/* <div className="text-center text-sm text-gray-500 dark:text-gray-400 pb-4">
            <p>This link is valid for 24 hours from the time it was created.</p>
            <p className="mt-2">Powered by RapidoRide</p>
          </div> */}
        </div>
      </div>
    </WebSocketProvider>
  );
}
